/**
 * Parses fenced-JSON specialist reports emitted by the pipeline-coordinator's
 * specialist agents (Wirer, Designer, Binder, Backend Architect, Render Fixer).
 *
 * Server-side schemas live in `mcp-chat-server/src/agents/shared/contracts.ts`
 * (`WirerReport`, `BinderReport`, `DesignerReport`, `BackendArchitectReport`,
 * `RenderFixerOutput`). We copy the shapes here instead of importing — the
 * designer and mcp-chat-server don't share a TypeScript package and creating
 * one for a render-only concern would be overkill. If those schemas change,
 * update the shapes here in the same change (the parser is permissive — extra
 * fields pass through, missing fields yield `null`).
 */

/** Names the pipeline tags `model_delta` events with. Anything else is treated as a fallback. */
export type SpecialistAgentName = "wirer" | "binder" | "designer" | "backend-architect" | "render-fixer";

export interface WirerAttachmentShape {
  triggerLabel: string;
  eventName: string;
  actionName: string;
  actionInstanceId?: string;
}

export interface BounceBackShape {
  to: string;
  reason: string;
  detail: string;
  intentReference: string;
}

export interface WirerReportShape {
  screenId: string;
  attachedActionCount: number;
  attachments: WirerAttachmentShape[];
  missingTriggers: string[];
  missingActions: string[];
  notes?: string;
  bounceBack?: BounceBackShape;
}

export interface BinderBindingShape {
  actionName: string;
  parameterName: string;
  bindingType: string;
  source: string;
}

export interface BinderUnboundShape {
  actionName: string;
  parameterName: string;
  reason: string;
}

export interface BinderReportShape {
  screenId: string;
  boundParameterCount: number;
  bindings: BinderBindingShape[];
  unboundParameters: BinderUnboundShape[];
  notes?: string;
  bounceBack?: BounceBackShape;
}

export interface DesignerReportShape {
  screenId: string;
  screenName: string;
  renderSucceeded: boolean;
  componentCount: number;
  boundViewCount: number;
  referencedActions: string[];
  designMode?: "bound" | "decorative" | string;
  notes?: string;
}

export interface BackendArchitectReportShape {
  schemaName: string;
  datasourceId: string;
  tablesCreated: string[];
  actionsCreated: string[];
}

export interface RenderFixerReportShape {
  appliedFixes: string[];
  /** Present in the server schema but not rendered — too long and not useful in chat. */
  fixedMarkup?: string;
}

/**
 * The `kind` discriminant is what narrows the union — `agent` alone can't do
 * it because the generic fallback's `agent: string` overlaps with the known
 * literals. `agent` is kept on every variant so callers can display the
 * original agent name (including unknown ones).
 */
export type ParsedSpecialistReport =
  | { kind: "wirer"; agent: "wirer"; data: WirerReportShape }
  | { kind: "binder"; agent: "binder"; data: BinderReportShape }
  | { kind: "designer"; agent: "designer"; data: DesignerReportShape }
  | { kind: "backend-architect"; agent: "backend-architect"; data: BackendArchitectReportShape }
  | { kind: "render-fixer"; agent: "render-fixer"; data: RenderFixerReportShape }
  | { kind: "generic"; agent: string; data: Record<string, unknown> };

// ── JSON extraction ─────────────────────────────────────────────────────────

/**
 * Pulls the first ```json fenced block out of `content`. Falls back to the
 * first `{ ... }` run if no fence is present — specialists occasionally
 * forget the fence and emit raw JSON.
 */
export function extractJsonBlock(content: string): string | null {
  if (!content) return null;

  const fenced = content.match(/```json\s*([\s\S]*?)```/i);
  if (fenced?.[1]) {
    return fenced[1].trim();
  }

  const genericFence = content.match(/```\s*(\{[\s\S]*?\})\s*```/);
  if (genericFence?.[1]) {
    return genericFence[1].trim();
  }

  // Last resort: find the first top-level { ... } run by bracket-counting.
  const start = content.indexOf("{");
  if (start === -1) return null;
  let depth = 0;
  let inString = false;
  let escape = false;
  for (let i = start; i < content.length; i++) {
    const ch = content[i];
    if (escape) {
      escape = false;
      continue;
    }
    if (ch === "\\") {
      escape = true;
      continue;
    }
    if (ch === '"') {
      inString = !inString;
      continue;
    }
    if (inString) continue;
    if (ch === "{") depth++;
    else if (ch === "}") {
      depth--;
      if (depth === 0) {
        return content.slice(start, i + 1);
      }
    }
  }
  return null;
}

function safeParseJson(raw: string): Record<string, unknown> | null {
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      return parsed as Record<string, unknown>;
    }
    return null;
  } catch {
    return null;
  }
}

// ── Narrow helpers ──────────────────────────────────────────────────────────

function asString(value: unknown, fallback = ""): string {
  return typeof value === "string" ? value : fallback;
}

function asNumber(value: unknown, fallback = 0): number {
  return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}

function asBoolean(value: unknown, fallback = false): boolean {
  return typeof value === "boolean" ? value : fallback;
}

function asStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((v): v is string => typeof v === "string");
}

function asObjectArray(value: unknown): Record<string, unknown>[] {
  if (!Array.isArray(value)) return [];
  return value.filter((v): v is Record<string, unknown> => v !== null && typeof v === "object" && !Array.isArray(v));
}

function asBounceBack(value: unknown): BounceBackShape | undefined {
  if (!value || typeof value !== "object" || Array.isArray(value)) return undefined;
  const obj = value as Record<string, unknown>;
  const to = asString(obj.to);
  const reason = asString(obj.reason);
  const detail = asString(obj.detail);
  const intentReference = asString(obj.intentReference);
  if (!to && !reason && !detail) return undefined;
  return { to, reason, detail, intentReference };
}

// ── Per-agent shape validators ──────────────────────────────────────────────

function toWirerReport(obj: Record<string, unknown>): WirerReportShape {
  return {
    screenId: asString(obj.screenId),
    attachedActionCount: asNumber(obj.attachedActionCount),
    attachments: asObjectArray(obj.attachments).map((a) => ({
      triggerLabel: asString(a.triggerLabel),
      eventName: asString(a.eventName),
      actionName: asString(a.actionName),
      actionInstanceId: typeof a.actionInstanceId === "string" ? a.actionInstanceId : undefined
    })),
    missingTriggers: asStringArray(obj.missingTriggers),
    missingActions: asStringArray(obj.missingActions),
    notes: typeof obj.notes === "string" ? obj.notes : undefined,
    bounceBack: asBounceBack(obj.bounceBack)
  };
}

function toBinderReport(obj: Record<string, unknown>): BinderReportShape {
  return {
    screenId: asString(obj.screenId),
    boundParameterCount: asNumber(obj.boundParameterCount),
    bindings: asObjectArray(obj.bindings).map((b) => ({
      actionName: asString(b.actionName),
      parameterName: asString(b.parameterName),
      bindingType: asString(b.bindingType),
      source: asString(b.source)
    })),
    unboundParameters: asObjectArray(obj.unboundParameters).map((u) => ({
      actionName: asString(u.actionName),
      parameterName: asString(u.parameterName),
      reason: asString(u.reason)
    })),
    notes: typeof obj.notes === "string" ? obj.notes : undefined,
    bounceBack: asBounceBack(obj.bounceBack)
  };
}

function toDesignerReport(obj: Record<string, unknown>): DesignerReportShape {
  const designMode = typeof obj.designMode === "string" ? obj.designMode : undefined;
  return {
    screenId: asString(obj.screenId),
    screenName: asString(obj.screenName),
    renderSucceeded: asBoolean(obj.renderSucceeded),
    componentCount: asNumber(obj.componentCount),
    boundViewCount: asNumber(obj.boundViewCount),
    referencedActions: asStringArray(obj.referencedActions),
    designMode,
    notes: typeof obj.notes === "string" ? obj.notes : undefined
  };
}

function toBackendArchitectReport(obj: Record<string, unknown>): BackendArchitectReportShape {
  return {
    schemaName: asString(obj.schemaName),
    datasourceId: asString(obj.datasourceId),
    tablesCreated: asStringArray(obj.tablesCreated),
    actionsCreated: asStringArray(obj.actionsCreated)
  };
}

function toRenderFixerReport(obj: Record<string, unknown>): RenderFixerReportShape {
  return {
    appliedFixes: asStringArray(obj.appliedFixes),
    fixedMarkup: typeof obj.fixedMarkup === "string" ? obj.fixedMarkup : undefined
  };
}

// ── Public parser ───────────────────────────────────────────────────────────

/**
 * Extracts and validates a specialist report from streaming assistant content.
 * Returns `null` when no JSON block is present or when the JSON is malformed
 * — callers should fall back to the raw content in that case.
 */
export function parseSpecialistReport(content: string, agent: string): ParsedSpecialistReport | null {
  const json = extractJsonBlock(content);
  if (!json) return null;

  const parsed = safeParseJson(json);
  if (!parsed) return null;

  switch (agent) {
    case "wirer":
      return { kind: "wirer", agent: "wirer", data: toWirerReport(parsed) };
    case "binder":
      return { kind: "binder", agent: "binder", data: toBinderReport(parsed) };
    case "designer":
      return { kind: "designer", agent: "designer", data: toDesignerReport(parsed) };
    case "backend-architect":
      return { kind: "backend-architect", agent: "backend-architect", data: toBackendArchitectReport(parsed) };
    case "render-fixer":
      return { kind: "render-fixer", agent: "render-fixer", data: toRenderFixerReport(parsed) };
    default:
      return { kind: "generic", agent, data: parsed };
  }
}

// ── Summary helpers (used by the accordion header pill) ─────────────────────

export interface SpecialistReportSummary {
  /** Short phrase rendered in the closed accordion header, e.g. "3 actions attached · 0 missing". */
  headline: string;
  /** Whether the report indicates a problem (error tone). */
  hasIssues: boolean;
}

export function summarizeSpecialistReport(report: ParsedSpecialistReport): SpecialistReportSummary {
  switch (report.kind) {
    case "wirer": {
      const d = report.data;
      const missing = d.missingTriggers.length + d.missingActions.length;
      const parts = [`${d.attachedActionCount} action${d.attachedActionCount === 1 ? "" : "s"} attached`];
      if (missing > 0) parts.push(`${missing} missing`);
      if (d.bounceBack) parts.push(`bouncing to ${d.bounceBack.to}`);
      return { headline: parts.join(" · "), hasIssues: missing > 0 || !!d.bounceBack };
    }
    case "binder": {
      const d = report.data;
      const unbound = d.unboundParameters.length;
      const parts = [`${d.boundParameterCount} param${d.boundParameterCount === 1 ? "" : "s"} bound`];
      if (unbound > 0) parts.push(`${unbound} unbound`);
      if (d.bounceBack) parts.push(`bouncing to ${d.bounceBack.to}`);
      return { headline: parts.join(" · "), hasIssues: unbound > 0 || !!d.bounceBack };
    }
    case "designer": {
      const d = report.data;
      const parts = [
        `${d.componentCount} component${d.componentCount === 1 ? "" : "s"}`,
        `${d.boundViewCount} bound view${d.boundViewCount === 1 ? "" : "s"}`
      ];
      if (d.designMode) parts.push(d.designMode);
      return { headline: parts.join(" · "), hasIssues: !d.renderSucceeded };
    }
    case "backend-architect": {
      const d = report.data;
      const tables = d.tablesCreated.length;
      const actions = d.actionsCreated.length;
      return {
        headline: `${tables} table${tables === 1 ? "" : "s"} · ${actions} action${actions === 1 ? "" : "s"}`,
        hasIssues: false
      };
    }
    case "render-fixer": {
      const fixes = report.data.appliedFixes.length;
      return {
        headline: `${fixes} fix${fixes === 1 ? "" : "es"} applied`,
        hasIssues: false
      };
    }
    default:
      return { headline: "Report ready", hasIssues: false };
  }
}

/** Friendly agent display name for the accordion header (Title-cased). */
export function formatAgentDisplayName(agent: string): string {
  const canonical: Record<string, string> = {
    wirer: "Wirer",
    binder: "Binder",
    designer: "Designer",
    "backend-architect": "Backend Architect",
    "render-fixer": "Render Fixer",
    // KOA-18193 — the post-build documentation pass. Not a build specialist,
    // but it runs on the same transcript surface and the user is waiting on it.
    "project-discovery": "Project Analysis"
  };
  if (canonical[agent]) return canonical[agent];
  return agent
    .split("-")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}
